import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Key, Mail, Link as LinkIcon, Pencil, UserX, UserCheck, Trash2, Search, Plus } from 'lucide-react';
import { api } from '../api/config';
import { usePermissions } from '../hooks/usePermissions';
import { useCustomerQuery } from '../hooks/useCustomerQuery';
import { useDeletedCustomerUsersCount } from '../api/hooks/customerUsers';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from '@license-management/ui';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { PermissionGate } from '../components/common/PermissionGate';
import ActionMenu, { ActionMenuSection } from '../components/common/ActionMenu';

interface CustomerUser {
  id: string;
  customerId: string;
  customerName?: string;
  email: string;
  name: string | null;
  status: 'active' | 'inactive';
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateCustomerUserForm {
  customerId: string;
  email: string;
  name: string;
  temporaryPassword: string;
}

const CustomerUsersManagement: React.FC = () => {
  usePermissions();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => Promise<void>>();
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch trash count for recycle bin badge
  const { data: trashCountData } = useDeletedCustomerUsersCount();
  const trashCount = trashCountData?.count || 0;

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordModalUser, setPasswordModalUser] = useState<CustomerUser | null>(null);
  const [passwordModalMode, setPasswordModalMode] = useState<'send' | 'generate' | 'set'>('send');
  const [generatedResetLink, setGeneratedResetLink] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  const [copied, setCopied] = useState(false);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CustomerUser | null>(null);
  const [editForm, setEditForm] = useState({ email: '', name: '', status: 'active' as 'active' | 'inactive' });

  const [newUser, setNewUser] = useState<CreateCustomerUserForm>({
    customerId: '',
    email: '',
    name: '',
    temporaryPassword: '',
  });

  // Fetch customer users
  const { data: customerUsers = [], isLoading } = useQuery({
    queryKey: ['customerUsers'],
    queryFn: () => api.get<CustomerUser[]>('/api/v1/admin/customer-users'),
  });

  // Fetch customers for the dropdown
  const { customers = [] } = useCustomerQuery({ includeDeleted: false });
  const activeCustomers = customers.filter(c => !c.deletedAt);

  // Create customer user mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerUserForm) =>
      api.post<CustomerUser>('/api/v1/admin/customer-users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setIsCreateModalOpen(false);
      setNewUser({ customerId: '', email: '', name: '', temporaryPassword: '' });
      setSuccess('Customer portal user created successfully');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Deactivate customer user mutation
  const deactivateMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/v1/admin/customer-users/${userId}/deactivate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setSuccess('User deactivated successfully');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Reactivate customer user mutation
  const reactivateMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/v1/admin/customer-users/${userId}/reactivate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setSuccess('User reactivated successfully');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Send reset link mutation (sends email)
  const sendResetLinkMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ message: string; resetLink: string; expiresIn: string }>(
        `/api/v1/admin/customer-users/${userId}/reset-password`,
        {}
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setGeneratedResetLink(data.resetLink);
      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 5000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Generate reset link mutation (no email)
  const generateResetLinkMutation = useMutation({
    mutationFn: (userId: string) =>
      api.post<{ message: string; resetLink: string; expiresIn: string }>(
        `/api/v1/admin/customer-users/${userId}/generate-reset-link`,
        {}
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setGeneratedResetLink(data.resetLink);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Set password mutation
  const setPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      api.post<{ message: string }>(
        `/api/v1/admin/customer-users/${userId}/set-password`,
        { password }
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setIsPasswordModalOpen(false);
      setManualPassword('');
      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 5000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Delete customer user mutation (soft delete)
  const deleteMutation = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/api/v1/admin/customer-users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      queryClient.invalidateQueries({ queryKey: ['customerUsers', 'trash'] });
      setSuccess('User moved to recycle bin');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  // Update customer user mutation
  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { email?: string; name?: string; status?: 'active' | 'inactive' } }) =>
      api.patch<CustomerUser>(`/api/v1/admin/customer-users/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerUsers'] });
      setIsEditModalOpen(false);
      setEditingUser(null);
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    },
  });

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    createMutation.mutate(newUser);
  };

  const handleDeactivate = (user: CustomerUser) => {
    setConfirmTitle('Deactivate User');
    setConfirmMessage(`Are you sure you want to deactivate "${user.email}"?`);
    setConfirmAction(() => async () => {
      await deactivateMutation.mutateAsync(user.id);
      setIsConfirmDialogOpen(false);
    });
    setIsConfirmDialogOpen(true);
  };

  const handleReactivate = (user: CustomerUser) => {
    setConfirmTitle('Reactivate User');
    setConfirmMessage(`Are you sure you want to reactivate "${user.email}"?`);
    setConfirmAction(() => async () => {
      await reactivateMutation.mutateAsync(user.id);
      setIsConfirmDialogOpen(false);
    });
    setIsConfirmDialogOpen(true);
  };

  const handleSendResetLink = (user: CustomerUser) => {
    setConfirmTitle('Send Reset Link');
    setConfirmMessage(`Send a password reset link to "${user.email}"? The user will receive an email with a link to set a new password.`);
    setConfirmAction(() => async () => {
      await sendResetLinkMutation.mutateAsync(user.id);
      setIsConfirmDialogOpen(false);
      // Open modal to show the reset link for backup
      setPasswordModalUser(user);
      setPasswordModalMode('send');
      setIsPasswordModalOpen(true);
    });
    setIsConfirmDialogOpen(true);
  };

  const handleGenerateResetLink = (user: CustomerUser) => {
    setPasswordModalUser(user);
    setPasswordModalMode('generate');
    setGeneratedResetLink('');
    setCopied(false);
    setIsPasswordModalOpen(true);
  };

  const handleSetPassword = (user: CustomerUser) => {
    setPasswordModalUser(user);
    setPasswordModalMode('set');
    setManualPassword('');
    setIsPasswordModalOpen(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedResetLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
      setTimeout(() => setError(''), 3000);
    }
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setPasswordModalUser(null);
    setGeneratedResetLink('');
    setManualPassword('');
    setCopied(false);
  };

  const handleEdit = (user: CustomerUser) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      name: user.name || '',
      status: user.status,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setError('');
    updateMutation.mutate({
      userId: editingUser.id,
      data: {
        email: editForm.email,
        name: editForm.name || undefined,
        status: editForm.status,
      },
    });
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setEditForm({ email: '', name: '', status: 'active' });
  };

  const handleDelete = (user: CustomerUser) => {
    setConfirmTitle('Move to Recycle Bin');
    setConfirmMessage(`Move "${user.email}" to the recycle bin? They can be restored within 30 days.`);
    setConfirmAction(() => async () => {
      await deleteMutation.mutateAsync(user.id);
      setIsConfirmDialogOpen(false);
    });
    setIsConfirmDialogOpen(true);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser({ ...newUser, temporaryPassword: password });
  };

  // Filter users based on search
  const filteredUsers = customerUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get customer name helper
  const getCustomerName = (customerId: string) => {
    const customer = activeCustomers.find((c) => c.customerId === customerId);
    return customer?.name || 'Unknown';
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Portal Users"
        description="Manage users who access the customer portal"
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/customer-users/trash')}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Recycle Bin
              {trashCount > 0 && (
                <Badge variant="error" size="sm" className="ml-2">
                  {trashCount}
                </Badge>
              )}
            </Button>
            <PermissionGate resource="customer_users" action="create">
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create Portal User
              </Button>
            </PermissionGate>
          </div>
        }
      />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by email, name, or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4 text-red-700">{error}</div>
        </Card>
      )}
      {success && (
        <Card className="border-green-200 bg-green-50">
          <div className="p-4 text-green-700 font-mono">{success}</div>
        </Card>
      )}

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-zinc-500">No customer portal users found.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-zinc-200">
              <thead className="bg-zinc-50">
                <tr>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="hidden sm:table-cell px-3 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th scope="col" className="px-3 py-3 text-right text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 bg-white">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-zinc-900">
                      <div className="flex flex-col">
                        <span>{user.email}</span>
                        {user.mustChangePassword && (
                          <Badge variant="warning" size="sm" className="mt-1 w-fit">
                            Must change password
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-zinc-500">
                      {user.name || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-zinc-500">
                      {user.customerName || getCustomerName(user.customerId)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4">
                      <Badge variant={user.status === 'active' ? 'success' : 'error'}>
                        {user.status}
                      </Badge>
                    </td>
                    <td className="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-zinc-500">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGate resource="customer_users" action="update">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            leftIcon={<Pencil className="h-4 w-4" />}
                          >
                            Edit
                          </Button>
                        </PermissionGate>
                        <PermissionGate resource="customer_users" action="delete">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(user)}
                          >
                            Delete
                          </Button>
                        </PermissionGate>
                        <ActionMenu
                          sections={[
                            {
                              title: 'Password',
                              items: [
                                {
                                  label: 'Send Reset Link',
                                  icon: <Mail className="h-4 w-4" />,
                                  onClick: () => handleSendResetLink(user),
                                  permission: { resource: 'customer_users', action: 'update' },
                                },
                                {
                                  label: 'Generate Reset Link',
                                  icon: <LinkIcon className="h-4 w-4" />,
                                  onClick: () => handleGenerateResetLink(user),
                                  permission: { resource: 'customer_users', action: 'update' },
                                },
                                {
                                  label: 'Set Password',
                                  icon: <Key className="h-4 w-4" />,
                                  onClick: () => handleSetPassword(user),
                                  permission: { resource: 'customer_users', action: 'update' },
                                },
                              ],
                            },
                            {
                              title: 'Account',
                              items: user.status === 'active'
                                ? [
                                    {
                                      label: 'Deactivate',
                                      icon: <UserX className="h-4 w-4" />,
                                      onClick: () => handleDeactivate(user),
                                      permission: { resource: 'customer_users', action: 'update' },
                                    },
                                  ]
                                : [
                                    {
                                      label: 'Reactivate',
                                      icon: <UserCheck className="h-4 w-4" />,
                                      onClick: () => handleReactivate(user),
                                      permission: { resource: 'customer_users', action: 'update' },
                                    },
                                  ],
                            },
                          ] as ActionMenuSection[]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create User Modal */}
      <Modal open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Create Customer Portal User</ModalTitle>
          </ModalHeader>
          <form onSubmit={handleCreateUser}>
            <ModalBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Customer
                </label>
                <select
                  required
                  value={newUser.customerId}
                  onChange={(e) =>
                    setNewUser({ ...newUser, customerId: e.target.value })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Select a customer...</option>
                  {activeCustomers.map((customer) => (
                    <option key={customer.customerId} value={customer.customerId}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Email
                </label>
                <Input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Name
                </label>
                <Input
                  type="text"
                  value={newUser.name}
                  onChange={(e) =>
                    setNewUser({ ...newUser, name: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Temporary Password (optional)
                </label>
                <div className="mt-1 flex">
                  <Input
                    type="text"
                    value={newUser.temporaryPassword}
                    onChange={(e) =>
                      setNewUser({ ...newUser, temporaryPassword: e.target.value })
                    }
                    placeholder="Leave blank to auto-generate"
                    minLength={8}
                    className="rounded-r-none font-mono"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={generatePassword}
                    className="rounded-l-none border-l-0"
                  >
                    Generate
                  </Button>
                </div>
                <p className="mt-1 text-xs text-zinc-500">
                  Leave blank to auto-generate. Must be at least 8 characters if provided. The user will be required to change this password on first login.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewUser({
                    customerId: '',
                    email: '',
                    name: '',
                    temporaryPassword: '',
                  });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create User'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onConfirm={async () => {
          if (confirmAction) {
            await confirmAction();
          }
        }}
        onCancel={() => setIsConfirmDialogOpen(false)}
        title={confirmTitle}
        message={confirmMessage}
      />

      {/* Password Actions Modal */}
      <Modal open={isPasswordModalOpen && !!passwordModalUser} onOpenChange={(open) => !open && closePasswordModal()}>
        <ModalContent className="max-w-lg">
          <ModalHeader>
            <ModalTitle>
              {passwordModalMode === 'send' && 'Reset Link Sent'}
              {passwordModalMode === 'generate' && 'Generate Reset Link'}
              {passwordModalMode === 'set' && 'Set Password'}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-zinc-600 mb-4">
              User: <span className="font-medium">{passwordModalUser?.email}</span>
            </p>

            {/* Send Reset Link - Show result */}
            {passwordModalMode === 'send' && generatedResetLink && (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    A password reset link has been sent to the user's email.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Backup: Copy this link if the email doesn't arrive
                  </label>
                  <div className="flex">
                    <Input
                      type="text"
                      readOnly
                      value={generatedResetLink}
                      className="rounded-r-none bg-zinc-50 text-sm font-mono"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCopyLink}
                      className="rounded-l-none border-l-0"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-zinc-500">
                    Link expires in 15 minutes
                  </p>
                </div>
              </div>
            )}

            {/* Generate Reset Link - Generate and show */}
            {passwordModalMode === 'generate' && (
              <div className="space-y-4">
                {!generatedResetLink ? (
                  <div>
                    <p className="text-sm text-zinc-600 mb-4">
                      Generate a password reset link to share manually with the user.
                      No email will be sent.
                    </p>
                    <Button
                      onClick={() => passwordModalUser && generateResetLinkMutation.mutate(passwordModalUser.id)}
                      disabled={generateResetLinkMutation.isPending}
                    >
                      {generateResetLinkMutation.isPending ? 'Generating...' : 'Generate Link'}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">
                      Password Reset Link
                    </label>
                    <div className="flex">
                      <Input
                        type="text"
                        readOnly
                        value={generatedResetLink}
                        className="rounded-r-none bg-zinc-50 text-sm font-mono"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleCopyLink}
                        className="rounded-l-none border-l-0"
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      Link expires in 15 minutes. Share this link with the user.
                    </p>
                    {copied && (
                      <p className="mt-2 text-sm text-green-600 font-medium">
                        Copied to clipboard!
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Set Password - Form */}
            {passwordModalMode === 'set' && (
              <form
                id="set-password-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (passwordModalUser) {
                    setPasswordMutation.mutate({
                      userId: passwordModalUser.id,
                      password: manualPassword,
                    });
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    New Password
                  </label>
                  <Input
                    type="text"
                    value={manualPassword}
                    onChange={(e) => setManualPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="font-mono"
                    minLength={8}
                    required
                  />
                  <p className="mt-1 text-xs text-zinc-500">
                    Minimum 8 characters. The user will not be required to change this password.
                  </p>
                </div>
              </form>
            )}
          </ModalBody>
          <ModalFooter>
            {passwordModalMode === 'set' ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closePasswordModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="set-password-form"
                  disabled={setPasswordMutation.isPending || manualPassword.length < 8}
                >
                  {setPasswordMutation.isPending ? 'Setting...' : 'Set Password'}
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={closePasswordModal}>
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit User Modal */}
      <Modal open={isEditModalOpen && !!editingUser} onOpenChange={(open) => !open && closeEditModal()}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Edit Customer Portal User</ModalTitle>
          </ModalHeader>
          <form onSubmit={handleUpdateUser}>
            <ModalBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Email
                </label>
                <Input
                  type="email"
                  required
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  className="mt-1"
                  disabled={updateMutation.isPending}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Name
                </label>
                <Input
                  type="text"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  className="mt-1"
                  disabled={updateMutation.isPending}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, status: e.target.value as 'active' | 'inactive' })
                  }
                  className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  disabled={updateMutation.isPending}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={closeEditModal}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CustomerUsersManagement;
